/**
 * LINDIE RSS Player
 * Fetches podcast episodes from an RSS feed via rss2json API.
 * Renders an episode list and handles inline HTML5 audio playback.
 *
 * Expects:
 *   <div data-lindie-podcast data-rss-url="https://...">
 *     <div data-podcast-loading>...</div>
 *     <p  data-podcast-error hidden></p>
 *     <div data-podcast-episodes hidden></div>
 *     <span data-podcast-count></span>
 *     <div data-podcast-player hidden>
 *       <button data-player-playpause>...</button>
 *       <p data-player-title></p>
 *       <input data-player-progress type="range">
 *       <span data-player-time></span>
 *       <button data-player-close></button>
 *     </div>
 *   </div>
 */

(function () {
  'use strict';

  // ── RSS proxy URL ────────────────────────────────────────────────────────────
  // Set this to your deployed Lambda Function URL once deployed.
  // Leave empty to fall back to rss2json (limited to 25 feeds on free tier).
  // Deploy: lambda/rss-proxy/deploy.sh  →  copy the Function URL here.
  const RSS_PROXY_URL = 'https://y4tvmofzzybig67biwxkzuczzq0jbvlt.lambda-url.us-east-1.on.aws';

  const RSS2JSON_BASE = 'https://api.rss2json.com/v1/api.json?rss_url=';

  function buildProxyUrl(feedUrl) {
    if (RSS_PROXY_URL) return RSS_PROXY_URL + '?url=' + encodeURIComponent(feedUrl);
    return RSS2JSON_BASE + encodeURIComponent(feedUrl);
  }

  // ── State ───────────────────────────────────────────────────────────────────
  let currentAudio      = null;
  let currentEpisodeEl  = null;
  let playerSection     = null;
  let playerBar         = null;
  let playerTitle       = null;
  let playerProgress    = null;
  let playerTime        = null;
  let playerPlayPause   = null;
  let playerSpeed       = null;

  const SPEEDS     = [1, 1.25, 1.5, 1.75, 2];
  let speedIdx     = 0;

  // ── Boot ────────────────────────────────────────────────────────────────────
  function init() {
    const section = document.querySelector('[data-lindie-podcast]');
    if (!section) return;

    const feedUrl = section.dataset.rssUrl;
    if (!feedUrl) return;

    playerSection   = section;
    playerBar       = section.querySelector('[data-podcast-player]');
    playerTitle     = playerBar && playerBar.querySelector('[data-player-title]');
    playerProgress  = playerBar && playerBar.querySelector('[data-player-progress]');
    playerTime      = playerBar && playerBar.querySelector('[data-player-time]');
    playerPlayPause = playerBar && playerBar.querySelector('[data-player-playpause]');
    playerSpeed     = playerBar && playerBar.querySelector('[data-player-speed]');

    // Close button
    const closeBtn = playerBar && playerBar.querySelector('[data-player-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', stopPlayback);
    }

    // Player progress scrub
    if (playerProgress) {
      playerProgress.addEventListener('input', onProgressScrub);
    }

    // Player play/pause button
    if (playerPlayPause) {
      playerPlayPause.addEventListener('click', togglePlayback);
    }

    // Speed button
    if (playerSpeed) {
      playerSpeed.addEventListener('click', cycleSpeed);
    }

    loadEpisodes(section, feedUrl);
  }

  // ── Load episodes from rss2json ─────────────────────────────────────────────
  async function loadEpisodes(section, feedUrl) {
    const loadingEl  = section.querySelector('[data-podcast-loading]');
    const errorEl    = section.querySelector('[data-podcast-error]');
    const listEl     = section.querySelector('[data-podcast-episodes]');

    try {
      const apiUrl = buildProxyUrl(feedUrl);
      const res    = await fetch(apiUrl);
      if (!res.ok) throw new Error('Network error: ' + res.status);
      const data = await res.json();
      if (data.status !== 'ok') throw new Error(data.message || 'Feed unavailable');

      if (loadingEl) loadingEl.hidden = true;
      renderEpisodes(section, listEl, data.items || []);

    } catch (err) {
      if (loadingEl) loadingEl.hidden = true;
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = 'Could not load episodes — please try again later.';
      }
      console.error('[LINDIE RSS]', err);
    }
  }

  // ── Render episode list ─────────────────────────────────────────────────────
  function renderEpisodes(section, listEl, items) {
    const countEl = section.querySelector('[data-podcast-count]');
    if (countEl) {
      countEl.textContent = items.length + ' episode' + (items.length !== 1 ? 's' : '');
    }

    if (!items.length) {
      listEl.innerHTML = '<p class="lmp-error">No episodes found in this feed.</p>';
      listEl.hidden = false;
      return;
    }

    listEl.innerHTML = items.map((item, idx) => {
      const audioUrl  = (item.enclosure && item.enclosure.link) || '';
      const title     = item.title  || 'Untitled Episode';
      const date      = formatDate(item.pubDate);
      const duration  = formatDuration(item['itunes:duration'] || item.itunes_duration || '');
      const episodeNo = items.length - idx;
      const metaParts = [date, duration].filter(Boolean);

      return [
        '<div',
        '  class="lmp-episode' + (audioUrl ? '' : ' lmp-episode--no-audio') + '"',
        '  data-audio="' + escapeAttr(audioUrl) + '"',
        '  data-title="' + escapeAttr(title) + '"',
        '>',
        '  <button',
        '    class="lmp-episode__play-btn"',
        '    aria-label="Play ' + escapeAttr(title) + '"',
        (audioUrl ? '' : '    disabled'),
        '  >',
        '    <svg class="lmp-episode__play-icon" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M4 3l10 5-10 5V3z"/></svg>',
        '    <svg class="lmp-episode__pause-icon" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" style="display:none"><rect x="3" y="2" width="4" height="12" rx="1"/><rect x="9" y="2" width="4" height="12" rx="1"/></svg>',
        '  </button>',
        '  <div class="lmp-episode__num">' + episodeNo + '</div>',
        '  <div class="lmp-episode__info">',
        '    <div class="lmp-episode__title">' + escapeHTML(title) + '</div>',
        (metaParts.length ? '    <div class="lmp-episode__meta">' + metaParts.join(' · ') + '</div>' : ''),
        '  </div>',
        '</div>',
      ].join('\n');
    }).join('');

    listEl.hidden = false;

    // Delegate episode clicks
    listEl.addEventListener('click', onEpisodeClick);
  }

  // ── Click handler for episode rows ─────────────────────────────────────────
  function onEpisodeClick(e) {
    const episodeEl = e.target.closest('[data-audio]');
    if (!episodeEl) return;
    const audioUrl = episodeEl.dataset.audio;
    if (!audioUrl) return;

    if (currentEpisodeEl === episodeEl && currentAudio) {
      // Toggle same episode
      currentAudio.paused ? currentAudio.play() : currentAudio.pause();
    } else {
      playEpisode(episodeEl, audioUrl);
    }
  }

  // ── Core play logic ─────────────────────────────────────────────────────────
  function playEpisode(episodeEl, audioUrl) {
    // Stop & reset previous
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.src = '';
      setEpisodeState(currentEpisodeEl, 'idle');
    }

    currentAudio     = new Audio(audioUrl);
    currentAudio.playbackRate = SPEEDS[speedIdx];
    currentEpisodeEl = episodeEl;

    setEpisodeState(episodeEl, 'playing');
    showPlayerBar(episodeEl.dataset.title);

    currentAudio.addEventListener('play',  onAudioPlay);
    currentAudio.addEventListener('pause', onAudioPause);
    currentAudio.addEventListener('ended', onAudioEnded);
    currentAudio.addEventListener('timeupdate', onTimeUpdate);

    currentAudio.play().catch(err => {
      console.warn('[LINDIE RSS] play() blocked:', err);
    });
  }

  function togglePlayback() {
    if (!currentAudio) return;
    currentAudio.paused ? currentAudio.play() : currentAudio.pause();
  }

  function stopPlayback() {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.src = '';
      currentAudio = null;
    }
    if (currentEpisodeEl) {
      setEpisodeState(currentEpisodeEl, 'idle');
      currentEpisodeEl = null;
    }
    if (playerBar) playerBar.hidden = true;
    speedIdx = 0;
    updateSpeedBtn();
  }

  function cycleSpeed() {
    speedIdx = (speedIdx + 1) % SPEEDS.length;
    if (currentAudio) currentAudio.playbackRate = SPEEDS[speedIdx];
    updateSpeedBtn();
  }

  function updateSpeedBtn() {
    if (!playerSpeed) return;
    const s = SPEEDS[speedIdx];
    playerSpeed.textContent = s + 'x';
    playerSpeed.setAttribute('aria-label', 'Playback speed: ' + s + 'x');
    playerSpeed.classList.toggle('lmp-player__speed--active', s !== 1);
  }

  // ── Audio event handlers ─────────────────────────────────────────────────────
  function onAudioPlay()  { setEpisodeState(currentEpisodeEl, 'playing'); setPlayerIcon(false); }
  function onAudioPause() { setEpisodeState(currentEpisodeEl, 'paused');  setPlayerIcon(true);  }
  function onAudioEnded() {
    setEpisodeState(currentEpisodeEl, 'idle');
    if (playerBar) playerBar.hidden = true;
    currentAudio     = null;
    currentEpisodeEl = null;
  }
  function onTimeUpdate() {
    if (!currentAudio || !currentAudio.duration) return;
    const pct = (currentAudio.currentTime / currentAudio.duration) * 100;
    if (playerProgress) playerProgress.value = pct;
    if (playerTime) {
      playerTime.textContent =
        formatSeconds(currentAudio.currentTime) + ' / ' + formatSeconds(currentAudio.duration);
    }
  }

  function onProgressScrub() {
    if (!currentAudio || !currentAudio.duration) return;
    currentAudio.currentTime = (playerProgress.value / 100) * currentAudio.duration;
  }

  // ── UI helpers ──────────────────────────────────────────────────────────────
  function showPlayerBar(title) {
    if (!playerBar) return;
    if (playerTitle)    playerTitle.textContent = title || '';
    if (playerProgress) playerProgress.value = 0;
    if (playerTime)     playerTime.textContent = '0:00';
    playerBar.hidden = false;
    setPlayerIcon(false);
  }

  function setPlayerIcon(showPlay) {
    if (!playerPlayPause) return;
    const pauseIcon = playerPlayPause.querySelector('.lmp-player__pause-icon');
    const playIcon  = playerPlayPause.querySelector('.lmp-player__play-icon');
    if (pauseIcon) pauseIcon.style.display = showPlay ? 'none' : '';
    if (playIcon)  playIcon.style.display  = showPlay ? '' : 'none';
    playerPlayPause.setAttribute('aria-label', showPlay ? 'Play' : 'Pause');
  }

  function setEpisodeState(el, state) {
    if (!el) return;
    const playIcon  = el.querySelector('.lmp-episode__play-icon');
    const pauseIcon = el.querySelector('.lmp-episode__pause-icon');
    const isPlaying = state === 'playing';
    el.classList.toggle('lmp-episode--playing', isPlaying);
    if (playIcon)  playIcon.style.display  = isPlaying ? 'none' : '';
    if (pauseIcon) pauseIcon.style.display = isPlaying ? '' : 'none';
  }

  // ── Formatters ──────────────────────────────────────────────────────────────
  function formatDate(pubDate) {
    if (!pubDate) return '';
    try {
      return new Date(pubDate).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch { return ''; }
  }

  function formatDuration(dur) {
    if (!dur) return '';
    if (typeof dur === 'string' && /^\d+:\d+/.test(dur)) return dur;
    const secs = parseInt(dur, 10);
    return secs ? formatSeconds(secs) : '';
  }

  function formatSeconds(s) {
    s = Math.floor(s || 0);
    const h   = Math.floor(s / 3600);
    const m   = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return h + ':' + pad(m) + ':' + pad(sec);
    return m + ':' + pad(sec);
  }

  function pad(n) { return String(n).padStart(2, '0'); }

  function escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ── Kick off ─────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
